import type {
  ForexVendor,
  ForexListing,
  ForexReview,
  ForexReport,
} from "@/store/slices/forexSlice";

// Nigerian cities with coordinates
export const nigerianCities = [
  { name: "Lagos", state: "Lagos", lat: 6.5244, lng: 3.3792 },
  { name: "Abuja", state: "FCT", lat: 9.0765, lng: 7.3986 },
  { name: "Kano", state: "Kano", lat: 12.0022, lng: 8.592 },
  { name: "Ibadan", state: "Oyo", lat: 7.3775, lng: 3.947 },
  { name: "Port Harcourt", state: "Rivers", lat: 4.8156, lng: 7.0498 },
  { name: "Benin City", state: "Edo", lat: 6.335, lng: 5.6037 },
  { name: "Kaduna", state: "Kaduna", lat: 10.5105, lng: 7.4165 },
  { name: "Enugu", state: "Enugu", lat: 6.5244, lng: 7.5186 },
  { name: "Owerri", state: "Imo", lat: 5.4763, lng: 7.0259 },
  { name: "Aba", state: "Abia", lat: 5.1066, lng: 7.3667 },
];

// Supported currencies
export const supportedCurrencies = [
  { code: "NGN", name: "Nigerian Naira", symbol: "₦", type: "fiat" },
  { code: "USD", name: "US Dollar", symbol: "$", type: "fiat" },
  { code: "GBP", name: "British Pound", symbol: "£", type: "fiat" },
  { code: "EUR", name: "Euro", symbol: "€", type: "fiat" },
  { code: "GHS", name: "Ghana Cedi", symbol: "GH₵", type: "fiat" },
  { code: "KES", name: "Kenyan Shilling", symbol: "KSh", type: "fiat" },
  { code: "XOF", name: "West African CFA", symbol: "CFA", type: "fiat" },
  { code: "ZAR", name: "South African Rand", symbol: "R", type: "fiat" },
  { code: "USDT", name: "Tether", symbol: "USDT", type: "crypto" },
  { code: "BTC", name: "Bitcoin", symbol: "BTC", type: "crypto" },
  { code: "ETH", name: "Ethereum", symbol: "ETH", type: "crypto" },
  { code: "BNB", name: "Binance Coin", symbol: "BNB", type: "crypto" },
];

// Generate random coordinates near a city
const getRandomCoordinates = (
  city: (typeof nigerianCities)[0],
  radius: number = 0.05,
) => ({
  lat: city.lat + (Math.random() - 0.5) * radius,
  lng: city.lng + (Math.random() - 0.5) * radius,
});

// Generate business hours
const generateBusinessHours = () => ({
  monday: "8:00 AM - 6:00 PM",
  tuesday: "8:00 AM - 6:00 PM",
  wednesday: "8:00 AM - 6:00 PM",
  thursday: "8:00 AM - 6:00 PM",
  friday: "8:00 AM - 6:00 PM",
  saturday: "9:00 AM - 4:00 PM",
  sunday: "Closed",
});

// Sample vendor names
const vendorNames = [
  "Elite Exchange Bureau",
  "Prime Currency Services",
  "Global Money Exchange",
  "Trust Forex Limited",
  "Swift Currency Solutions",
  "Royal Exchange Hub",
  "Metro Forex Services",
  "Golden Rate Exchangers",
  "FastLink Currency",
  "Alpha Exchange Point",
  "Supreme Forex Bureau",
  "Unity Money Changers",
  "Peak Exchange Services",
  "Diamond Forex Limited",
  "Nova Currency Hub",
  "Starlight Exchange",
  "Capital Forex Bureau",
  "Liberty Money Exchange",
  "Heritage Currency Services",
  "Vista Forex Limited",
];

// Sample street addresses
const streetAddresses = [
  "15 Broad Street",
  "42 Marina Road",
  "8 Adeola Odeku Street",
  "23 Allen Avenue",
  "67 Obafemi Awolowo Way",
  "12 Toyin Street",
  "35 Opebi Road",
  "19 Adetokunbo Ademola Street",
  "56 Ahmadu Bello Way",
  "3 Bode Thomas Street",
  "28 Awolowo Road",
  "9 Kofo Abayomi Street",
  "44 Ogunlana Drive",
  "17 Akin Adesola Street",
  "62 Glover Road",
  "7 Bishop Aboyade Cole Street",
  "31 Idowu Martins Street",
  "14 Adeyemo Alakija Street",
  "48 Norman Williams Street",
  "21 Ribadu Road",
];

// Generate mock vendors
export const generateMockVendors = (): ForexVendor[] => {
  const vendors: ForexVendor[] = [];

  for (let i = 0; i < 20; i++) {
    const city = nigerianCities[i % nigerianCities.length];
    const coordinates = getRandomCoordinates(city);
    const isVerified = Math.random() > 0.3;
    const verificationBadge = isVerified
      ? Math.random() > 0.5
        ? "premium"
        : "basic"
      : "none";

    vendors.push({
      id: `vendor_${i + 1}`,
      shopName: vendorNames[i],
      ownerName: `Manager ${String.fromCharCode(65 + (i % 26))}. ${["Smith", "Johnson", "Williams", "Brown", "Jones"][i % 5]}`,
      email: `contact@${vendorNames[i].toLowerCase().replace(/\s+/g, "")}.com`,
      phone: `+234${80 + (i % 20)}${Math.floor(Math.random() * 10000000)
        .toString()
        .padStart(7, "0")}`,
      whatsapp: `+234${80 + (i % 20)}${Math.floor(Math.random() * 10000000)
        .toString()
        .padStart(7, "0")}`,
      location: {
        address: streetAddresses[i],
        city: city.name,
        state: city.state,
        coordinates,
      },
      businessHours: generateBusinessHours(),
      isVerified,
      verificationBadge,
      rating: 3.5 + Math.random() * 1.5,
      reviewCount: Math.floor(Math.random() * 200),
      avatar: `https://ui-avatars.com/api/?name=${vendorNames[i].replace(/\s+/g, "+")}&background=random`,
      createdAt: new Date(
        Date.now() - Math.random() * 365 * 24 * 60 * 60 * 1000,
      ).toISOString(),
      lastActive: new Date(
        Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000,
      ).toISOString(),
      totalViews: Math.floor(Math.random() * 10000),
      totalClicks: Math.floor(Math.random() * 5000),
      subscriptionType: ["free", "premium", "featured"][
        Math.floor(Math.random() * 3)
      ] as "free" | "premium" | "featured",
      subscriptionExpiry: new Date(
        Date.now() + Math.random() * 365 * 24 * 60 * 60 * 1000,
      ).toISOString(),
      walletBalance: Math.floor(Math.random() * 50000),
      status: Math.random() > 0.1 ? "active" : "pending",
    });
  }

  return vendors;
};

// Generate currency pairs with rates
const generateCurrencyPairs = () => {
  const pairs = [
    { from: "USD", to: "NGN", baseRate: 1550 },
    { from: "GBP", to: "NGN", baseRate: 1980 },
    { from: "EUR", to: "NGN", baseRate: 1680 },
    { from: "GHS", to: "NGN", baseRate: 95 },
    { from: "USDT", to: "NGN", baseRate: 1540 },
    { from: "BTC", to: "NGN", baseRate: 95000000 },
    { from: "ETH", to: "NGN", baseRate: 5200000 },
  ];

  return pairs.map((pair) => ({
    ...pair,
    buyRate: pair.baseRate * (0.98 + Math.random() * 0.04),
    sellRate: pair.baseRate * (1.02 + Math.random() * 0.04),
    minAmount: [100, 500, 1000, 5000][Math.floor(Math.random() * 4)],
    maxAmount: [50000, 100000, 500000, 1000000][Math.floor(Math.random() * 4)],
  }));
};

// Generate mock listings
export const generateMockListings = (
  vendors: ForexVendor[],
): ForexListing[] => {
  const listings: ForexListing[] = [];

  for (let i = 0; i < 40; i++) {
    const vendor = vendors[i % vendors.length];
    const isFeatured = i < 8;
    const isPromoted = i < 4;

    listings.push({
      id: `listing_${i + 1}`,
      vendorId: vendor.id,
      title: `${vendor.shopName} - Best Rates in ${vendor.location.city}`,
      description: `We offer competitive exchange rates for all major currencies. Fast, reliable, and secure service. ${isFeatured ? "Verified and trusted by thousands of customers." : ""} Visit us today for the best deals on USD, GBP, EUR, and cryptocurrency exchanges.`,
      currencies: generateCurrencyPairs(),
      location: vendor.location,
      images: [
        `https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&h=600&fit=crop`,
        `https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=800&h=600&fit=crop`,
      ],
      isFeatured,
      isPromoted,
      status: Math.random() > 0.05 ? "active" : "pending",
      views: Math.floor(Math.random() * 5000),
      clicks: Math.floor(Math.random() * 2000),
      createdAt: new Date(
        Date.now() - Math.random() * 180 * 24 * 60 * 60 * 1000,
      ).toISOString(),
      updatedAt: new Date(
        Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000,
      ).toISOString(),
      expiresAt: new Date(
        Date.now() + Math.random() * 90 * 24 * 60 * 60 * 1000,
      ).toISOString(),
    });
  }

  return listings;
};

// Sample review comments
const reviewComments = [
  "Excellent service! Very fast and reliable.",
  "Great rates compared to others in the area.",
  "Professional staff and secure location.",
  "Quick transaction, will definitely come back.",
  "Best exchange rates I have found in Lagos.",
  "Trustworthy and transparent pricing.",
  "Good experience overall, slightly busy during peak hours.",
  "Very helpful staff explained everything clearly.",
  "Fast service, competitive rates. Recommended!",
  "Had a small issue but was resolved quickly.",
  "Consistently good rates over the past year.",
  "Safe and secure location, feel comfortable trading here.",
  "They always have the currencies I need.",
  "Professional setup, legit business.",
  "Better rates than the banks!",
];

// Generate mock reviews
export const generateMockReviews = (
  listings: ForexListing[],
): ForexReview[] => {
  const reviews: ForexReview[] = [];

  for (let i = 0; i < 100; i++) {
    const listing = listings[i % listings.length];

    reviews.push({
      id: `review_${i + 1}`,
      listingId: listing.id,
      vendorId: listing.vendorId,
      reviewerName:
        [
          "John",
          "Mary",
          "David",
          "Sarah",
          "Michael",
          "Jennifer",
          "Chris",
          "Amanda",
        ][i % 8] +
        " " +
        ["A.", "B.", "C.", "D.", "E."][i % 5],
      reviewerAvatar: `https://ui-avatars.com/api/?name=User+${i + 1}&background=random`,
      rating: 3 + Math.floor(Math.random() * 3),
      comment: reviewComments[i % reviewComments.length],
      createdAt: new Date(
        Date.now() - Math.random() * 90 * 24 * 60 * 60 * 1000,
      ).toISOString(),
      isVerified: Math.random() > 0.3,
    });
  }

  return reviews;
};

// Report reasons
const reportReasons = [
  "Suspicious activity",
  "False advertising",
  "Unethical behavior",
  "Scam or fraud",
  "Wrong location",
  "Closed business",
  "Poor service",
  "Rate manipulation",
];

// Generate mock reports
export const generateMockReports = (
  listings: ForexListing[],
): ForexReport[] => {
  const reports: ForexReport[] = [];

  for (let i = 0; i < 10; i++) {
    const listing = listings[i % listings.length];

    reports.push({
      id: `report_${i + 1}`,
      listingId: listing.id,
      vendorId: listing.vendorId,
      reporterName: `Reporter ${i + 1}`,
      reporterPhone: `+23480${Math.floor(Math.random() * 10000000)
        .toString()
        .padStart(7, "0")}`,
      reason: reportReasons[i % reportReasons.length],
      details:
        "This vendor appears to be engaging in suspicious activities. Please investigate.",
      status: ["pending", "investigating", "resolved", "dismissed"][i % 4] as
        | "pending"
        | "investigating"
        | "resolved"
        | "dismissed",
      createdAt: new Date(
        Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000,
      ).toISOString(),
    });
  }

  return reports;
};

// Initialize mock data
export const initializeForexData = () => {
  const vendors = generateMockVendors();
  const listings = generateMockListings(vendors);
  const reviews = generateMockReviews(listings);
  const reports = generateMockReports(listings);

  // Store in localStorage
  if (typeof window !== "undefined") {
    localStorage.setItem("forex_vendors", JSON.stringify(vendors));
    localStorage.setItem("forex_listings", JSON.stringify(listings));
    localStorage.setItem("forex_reviews", JSON.stringify(reviews));
    localStorage.setItem("forex_reports", JSON.stringify(reports));
  }

  return { vendors, listings, reviews, reports };
};

// Get data from localStorage or initialize
export const getForexData = () => {
  if (typeof window === "undefined") {
    return initializeForexData();
  }

  const vendors = localStorage.getItem("forex_vendors");
  const listings = localStorage.getItem("forex_listings");
  const reviews = localStorage.getItem("forex_reviews");
  const reports = localStorage.getItem("forex_reports");

  if (!vendors || !listings || !reviews || !reports) {
    return initializeForexData();
  }

  return {
    vendors: JSON.parse(vendors),
    listings: JSON.parse(listings),
    reviews: JSON.parse(reviews),
    reports: JSON.parse(reports),
  };
};

// Initial data
export const {
  vendors: mockForexVendors,
  listings: mockForexListings,
  reviews: mockForexReviews,
  reports: mockForexReports,
} = initializeForexData();
